import React, { useState, useEffect } from 'react';
import { clientPortalAPI } from '../services/api';
import './ClientPages.css';

const ClientAccount = () => {
  const [account, setAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });

  useEffect(() => { loadAccount(); }, []);

  const loadAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await clientPortalAPI.getAccount();
      setAccount(response.data);
      try {
        const accts = await clientPortalAPI.getAccounts();
        setAccounts(accts.data || []);
      } catch (e) {
        // Accounts list is a nice-to-have - keep the page working without it
      }
    } catch (err) {
      console.error('Failed to load account:', err);
      setError("We couldn't load your account details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) {
      alert('Passwords do not match');
      return;
    }
    setSavingPassword(true);
    try {
      await clientPortalAPI.changePassword({
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      alert('Password changed successfully');
      setShowChangePassword(false);
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) return <div className="client-page-loading"><div className="client-loading-spinner"></div><p>Loading account details...</p></div>;

  if (error) {
    return (
      <div className="client-page-error client-error-block" role="alert">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <p>{error}</p>
        <button className="client-retry-btn" onClick={loadAccount}>Try Again</button>
      </div>
    );
  }

  if (!account) return null;

  return (
    <div className="client-page">
      <div className="client-page-header">
        <div>
          <h1>Account Details</h1>
          <p className="client-page-subtitle">Your account information</p>
        </div>
      </div>

      <div className="client-account-grid">
        {/* Bank Accounts */}
        <div className="client-card" style={{ gridColumn: '1 / -1' }}>
          <div className="client-card-header">
            <h3>Your Accounts</h3>
          </div>
          <div className="client-detail-list">
            {accounts.map((acc, idx) => {
              const typeLabel = String(acc.account_type || 'checking').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
              return (
                <div className="client-detail-row" key={acc.id || idx}>
                  <span className="client-detail-label">
                    {acc.account_name || `${typeLabel} Account`}
                    <span style={{ display: 'block', fontSize: '12px', color: '#64748b', fontFamily: 'monospace', letterSpacing: '0.06em', marginTop: '2px' }}>
                      {typeLabel} •••• {String(acc.account_number || '').slice(-4) || '—'}
                      {acc.is_primary ? ' · Primary' : ''}
                    </span>
                  </span>
                  <span className="client-detail-value" style={{ textAlign: 'right' }}>
                    <span className="client-detail-balance" style={{ display: 'block' }}>${parseFloat(acc.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: acc.status === 'frozen' ? '#f59e0b' : acc.status === 'closed' ? '#94a3b8' : '#10b981' }}>
                      {acc.status || 'active'}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Profile Card */}
        <div className="client-card client-profile-card">
          <div className="client-profile-avatar">
            {account.full_name?.charAt(0)?.toUpperCase()}
          </div>
          <h2 className="client-profile-name">{account.full_name}</h2>
          <span className={`client-status-badge status-${account.account_status}`}>
            {account.account_status?.charAt(0).toUpperCase() + account.account_status?.slice(1)}
          </span>
        </div>

        {/* Account Information */}
        <div className="client-card">
          <div className="client-card-header">
            <h3>Account Information</h3>
          </div>
          <div className="client-detail-list">
            <div className="client-detail-row">
              <span className="client-detail-label">Case ID</span>
              <span className="client-detail-value client-detail-mono">{account.case_id}</span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Account Type</span>
              <span className="client-detail-value">{account.account_type?.charAt(0).toUpperCase() + account.account_type?.slice(1)}</span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Account Status</span>
              <span className="client-detail-value">
                <span className={`client-status-dot status-${account.account_status}`}></span>
                {account.account_status?.charAt(0).toUpperCase() + account.account_status?.slice(1)}
              </span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Available Balance</span>
              <span className="client-detail-value client-detail-balance">
                ${parseFloat(account.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Member Since</span>
              <span className="client-detail-value">{formatDate(account.created_at)}</span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Last Login</span>
              <span className="client-detail-value">{account.last_login_at ? formatDate(account.last_login_at) : '—'}</span>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="client-card">
          <div className="client-card-header">
            <h3>Personal Information</h3>
          </div>
          <div className="client-detail-list">
            <div className="client-detail-row">
              <span className="client-detail-label">Full Name</span>
              <span className="client-detail-value">{account.full_name}</span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Email</span>
              <span className="client-detail-value">{account.email || '—'}</span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Phone</span>
              <span className="client-detail-value">{account.phone || '—'}</span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Address</span>
              <span className="client-detail-value">{account.address || '—'}</span>
            </div>
            <div className="client-detail-row">
              <span className="client-detail-label">Date of Birth</span>
              <span className="client-detail-value">{account.date_of_birth || '—'}</span>
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="client-card">
          <div className="client-card-header">
            <h3>Security</h3>
          </div>
          {!showChangePassword ? (
            <div className="client-security-section">
              <p className="client-security-text">Keep your account secure by regularly updating your password.</p>
              <button className="client-btn client-btn-outline" onClick={() => setShowChangePassword(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Change Password
              </button>
            </div>
          ) : (
            <form onSubmit={handlePasswordChange} className="client-password-form">
              <div className="client-form-field">
                <label>Current Password</label>
                <input type="password" value={passwords.current_password} onChange={(e) => setPasswords({...passwords, current_password: e.target.value})} required />
              </div>
              <div className="client-form-field">
                <label>New Password</label>
                <input type="password" value={passwords.new_password} onChange={(e) => setPasswords({...passwords, new_password: e.target.value})} required minLength={6} />
              </div>
              <div className="client-form-field">
                <label>Confirm New Password</label>
                <input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})} required minLength={6} />
              </div>
              <div className="client-form-actions">
                <button type="submit" className="client-btn client-btn-primary" disabled={savingPassword}>
                  {savingPassword ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'clientSpin 0.7s linear infinite' }}></span>
                      Saving...
                    </span>
                  ) : 'Save Password'}
                </button>
                <button type="button" className="client-btn client-btn-ghost" onClick={() => setShowChangePassword(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientAccount;
